'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { BreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BreadcrumbsFragmentFragmentDoc';
import { Breadcrumbs } from '@packages/shared/src/components/Breadcrumbs/Breadcrumbs';
import { Link } from '@packages/shared/src/components/Link/Link';
import { Stack } from '@packages/shared/src/components/Stack';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import type { PropsWithChildren } from 'react';
import { ExtendedBreadcrumbsBackButton } from './ExtendedBreadcrumbsBackButton';

// eslint-disable-next-line @typescript-eslint/naming-convention
/* GraphQL */ `
  fragment BreadcrumbsFragment on Breadcrumb {
    breadCrumb {
      name
      id
      uriPath
    }
  }
`;

export type ExtendedBreadcrumbsProps = {
  /** The breadcrumbs object. */
  breadCrumbData: FragmentType<typeof BreadcrumbsFragmentFragmentDoc> | undefined | null;
};

export const BreadcrumbsWrapper = ({ children }: PropsWithChildren) => (
  <>
    <div className="h-[38px] w-full">{children}</div>
    <hr className="text-color-grey mt-2 mb-1 w-full" />
  </>
);

/**
 * <p>This components provides a basic breadcrumbs navigation, to get back to the previous page.</p>
 * <p>In mobile, the breadcrumbs are condensed to 3 entries maximum.</p>
 */
export const ExtendedBreadcrumbs = ({ breadCrumbData }: ExtendedBreadcrumbsProps) => {
  const {
    host: { domain },
  } = useConfig();
  const { isMobile } = useDeviceType();

  const breadCrumb = breadCrumbData
    ? (getFragmentData(BreadcrumbsFragmentFragmentDoc, breadCrumbData).breadCrumb ?? [])
    : [];

  const hasBreadcrumb = breadCrumb.length > 0;

  return (
    <BreadcrumbsWrapper>
      <Stack gap={1} direction="row" alignItems="center">
        <ExtendedBreadcrumbsBackButton breadCrumbData={breadCrumb} />
        {hasBreadcrumb && !isMobile && (
          <>
            <Breadcrumbs>
              {breadCrumb.map(({ id, uriPath, name }) => (
                <Link key={id} href={uriPath} underline="hover">
                  {name}
                </Link>
              ))}
            </Breadcrumbs>
            <script
              type="application/ld+json"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  itemListElement: breadCrumb.map(({ uriPath, name }, index) => ({
                    item: { name, '@id': `https://www.${domain}${uriPath}` },
                    '@type': 'ListItem',
                    position: index + 1,
                  })),
                  '@type': 'BreadcrumbList',
                  '@context': 'http://schema.org',
                }),
              }}
            />
          </>
        )}
      </Stack>
    </BreadcrumbsWrapper>
  );
};
